import React from 'react';
import { QuoteIcon, StarIcon } from 'lucide-react';
interface Review {
  quote: string;
  author: string;
  role: string;
  rating: number;
  location: string;
}
function StarRating({
  rating
}: {
  rating: number;
}) {
  return <div className="flex gap-0.5" data-id="element-169">
      {[1, 2, 3, 4, 5].map(star => <StarIcon key={star} className={`h-5 w-5 ${star <= rating ? 'text-yellow-400 fill-yellow-400' : 'text-slate-300'}`} data-id="element-170" />)}
    </div>;
}
export function Testimonials() {
  const reviews: Review[] = [{
    quote: 'Universal Global Logistics supplied us with a professional courier driver within two hours of our call. Outstanding service and a reliable driver. We now use them every week.',
    author: 'James T.',
    role: 'Operations Manager',
    rating: 5,
    location: 'London'
  }, {
    quote: 'We hire their drivers regularly during peak demand periods. Always professional, always on time. A fantastic driver supply partner that we can depend on.',
    author: 'Sarah M.',
    role: 'Logistics Director',
    rating: 5,
    location: 'Manchester'
  }, {
    quote: 'Needed an emergency courier driver and item delivery at short notice — they delivered on both. Highly recommended to any business needing reliable drivers.',
    author: 'David R.',
    role: 'Business Owner',
    rating: 5,
    location: 'Birmingham'
  }, {
    quote: 'Brilliant service from start to finish. The driver arrived promptly, was courteous and handled our fragile items with great care. Will definitely use again.',
    author: 'Priya K.',
    role: 'E-commerce Manager',
    rating: 5,
    location: 'Leeds'
  }, {
    quote: 'We needed a team of drivers for our office relocation and Universal Global provided three experienced professionals at very short notice. Seamless experience.',
    author: 'Michael O.',
    role: 'Facilities Manager',
    rating: 5,
    location: 'Bristol'
  }, {
    quote: "As a small business, finding reliable courier drivers was always a challenge. Since partnering with Universal Global, we haven't looked back. Excellent driver quality.",
    author: 'Rachel W.',
    role: 'Warehouse Supervisor',
    rating: 4,
    location: 'Glasgow'
  }, {
    quote: 'They handled our furniture delivery and assembly perfectly. The driver was friendly, efficient, and left everything spotless. Great value for money.',
    author: 'Tom H.',
    role: 'Interior Designer',
    rating: 5,
    location: 'Edinburgh'
  }, {
    quote: 'Used their emergency courier service for a critical same-day delivery. The driver collected within 45 minutes and delivered across the country by evening. Lifesavers!',
    author: 'Aisha B.',
    role: 'Procurement Lead',
    rating: 5,
    location: 'Cardiff'
  }, {
    quote: 'Professional, punctual, and great communication throughout. The driver kept us updated at every stage. This is how logistics should work.',
    author: 'Chris L.',
    role: 'Supply Chain Manager',
    rating: 5,
    location: 'Newcastle'
  }];
  const averageRating = (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1);
  const fiveStarCount = reviews.filter(r => r.rating === 5).length;
  return <section className="py-20 bg-slate-50" data-id="element-171">
      <div className="container mx-auto px-4" data-id="element-172">
        <div className="text-center max-w-3xl mx-auto mb-12" data-id="element-173">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-174">
            Trusted by Businesses Across the UK
          </h2>
          <p className="text-lg text-slate-600" data-id="element-175">
            Don't just take our word for it — here's what our clients say about
            our drivers and delivery services.
          </p>
        </div>

        {/* Rating Summary */}
        <div className="max-w-2xl mx-auto mb-16" data-id="element-176">
          <div className="bg-white rounded-2xl shadow-md border border-slate-100 p-8 flex flex-col sm:flex-row items-center gap-8" data-id="element-177">
            <div className="text-center sm:text-left" data-id="element-178">
              <div className="text-6xl font-extrabold text-slate-900" data-id="element-179">
                {averageRating}
              </div>
              <StarRating rating={Math.round(Number(averageRating))} data-id="element-180" />
              <p className="text-sm text-slate-500 mt-2" data-id="element-181">
                Based on {reviews.length} reviews
              </p>
            </div>
            <div className="h-px sm:h-16 sm:w-px w-full bg-slate-200" data-id="element-182" />
            <div className="flex-1 text-center sm:text-left" data-id="element-183">
              <p className="text-2xl font-bold text-slate-900 mb-1" data-id="element-184">
                Excellent
              </p>
              <p className="text-slate-600" data-id="element-185">
                {fiveStarCount} out of {reviews.length} reviewers gave us 5
                stars. Our clients trust us to supply professional courier
                drivers and deliver their items safely.
              </p>
            </div>
          </div>
        </div>

        {/* Reviews Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-186">
          {reviews.map((review, index) => <div key={index} className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 relative hover:shadow-md transition-shadow" data-id="element-187">
              <QuoteIcon className="h-8 w-8 text-blue-100 absolute top-5 left-5" data-id="element-188" />
              <div className="relative z-10" data-id="element-189">
                <div className="flex items-center justify-between mb-4 pt-6" data-id="element-190">
                  <StarRating rating={review.rating} data-id="element-191" />
                </div>
                <p className="text-slate-700 leading-relaxed mb-5" data-id="element-192">
                  "{review.quote}"
                </p>
                <div className="flex items-center justify-between border-t border-slate-100 pt-4" data-id="element-193">
                  <div data-id="element-194">
                    <p className="font-bold text-slate-900" data-id="element-195">{review.author}</p>
                    <p className="text-sm text-primary font-medium" data-id="element-196">
                      {review.role}
                    </p>
                  </div>
                  <span className="text-xs text-slate-400 bg-slate-50 px-3 py-1 rounded-full" data-id="element-197">
                    {review.location}
                  </span>
                </div>
              </div>
            </div>)}
        </div>

        {/* Google Review CTA */}
        <div className="text-center mt-12" data-id="element-198">
          <div className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-full px-6 py-3 shadow-sm" data-id="element-199">
            <div className="flex gap-0.5" data-id="element-200">
              {[1, 2, 3, 4, 5].map(star => <StarIcon key={star} className="h-4 w-4 text-yellow-400 fill-yellow-400" data-id="element-201" />)}
            </div>
            <span className="text-slate-700 font-medium" data-id="element-202">
              Rated {averageRating}/5 by our clients
            </span>
          </div>
        </div>
      </div>
    </section>;
}